import { useState, useEffect } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Services from './components/Services';
import WhyUs from './components/WhyUs';
import ServiceAreas from './components/ServiceAreas';
import Pricing from './components/Pricing';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';

const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) || 'https://mowandsnow.ca';

const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': 'LandscapingBusiness',
  '@id': `${siteUrl}/#business`,
  name: 'Mow and Snow',
  alternateName: 'Mow and Snow Co',
  url: `${siteUrl}/`,
  logo: 'https://static.readdy.ai/image/096be9c57cd562040b50de9c711536fc/281fbea3ff24195271af1eb3bd4da899.jpeg',
  image: 'https://static.readdy.ai/image/096be9c57cd562040b50de9c711536fc/893f408264c7f48708f4fd5a72a9bdbf.png',
  description:
    'Professional landscaping and snow removal services throughout the Greater Toronto Area. One flat monthly rate covers lawn mowing, hardscaping, fertilizing, fall cleanup, and snow plowing.',
  telephone: '+16479837924',
  email: 'info@mowandsnow.co',
  foundingDate: '2010',
  priceRange: '$$',
  currenciesAccepted: 'CAD',
  paymentAccepted: 'Cash, Credit Card, E-Transfer',
  areaServed: [
    { '@type': 'City', name: 'Toronto', sameAs: 'https://www.wikidata.org/wiki/Q172' },
    { '@type': 'City', name: 'Richmond Hill' },
    { '@type': 'City', name: 'Markham' },
    { '@type': 'City', name: 'Stouffville' },
    { '@type': 'City', name: 'Aurora' },
    { '@type': 'City', name: 'Newmarket' },
    { '@type': 'City', name: 'Vaughan' },
    { '@type': 'City', name: 'Mississauga' },
    { '@type': 'City', name: 'Scarborough' },
    { '@type': 'City', name: 'Pickering' },
    { '@type': 'City', name: 'Ajax' },
    { '@type': 'City', name: 'Whitby' },
    { '@type': 'City', name: 'Oshawa' },
    { '@type': 'City', name: 'Thornhill' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Toronto',
    addressRegion: 'ON',
    addressCountry: 'CA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 43.6532,
    longitude: -79.3832,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00',
      closes: '19:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday', 'Sunday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  sameAs: ['https://www.instagram.com/mowandsnowco'],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Landscaping & Snow Removal Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lawn Mowing & Edging' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Snow Removal & Plowing' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Fall Cleanup & Leaf Removal' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Hardscaping & Interlocking' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Fertilizing & Weed Control' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Irrigation Systems' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tree & Shrub Care' } },
    ],
  },
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${siteUrl}/#website`,
  url: `${siteUrl}/`,
  name: 'Mow and Snow',
  description: 'Professional landscaping and snow removal services in the Greater Toronto Area',
  publisher: { '@id': `${siteUrl}/#business` },
  inLanguage: 'en-CA',
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What areas does Mow and Snow serve?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Mow and Snow serves the entire Greater Toronto Area including Toronto, Richmond Hill, Markham, Stouffville, Aurora, Newmarket, Vaughan, Mississauga, Scarborough, Pickering, Ajax, Whitby, Oshawa, and Thornhill.',
      },
    },
    {
      '@type': 'Question',
      name: 'What is included in the monthly landscaping plan?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our monthly plans include lawn mowing, edge trimming, organic fertilizing, fall leaf pickup, snow removal, and more depending on the plan tier. All services are covered for one flat monthly rate with no hidden fees.',
      },
    },
    {
      '@type': 'Question',
      name: 'How much does lawn mowing and snow removal cost in Toronto?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Our Basic plan starts at $99/month, the Complete plan at $169/month, and the Premium plan at $299/month. Commercial and large estate pricing is custom — contact us for a free quote.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is Mow and Snow licensed and insured?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Mow and Snow is fully licensed and insured, providing professional landscaping and snow removal services across the GTA since 2010.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do you offer hardscaping and interlocking services?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. We offer full hardscaping services including interlocking driveways and patios starting at $14 per square foot, retaining walls, garden design, and more.',
      },
    },
  ],
};

const serviceSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Landscaping and Snow Removal',
  provider: { '@id': `${siteUrl}/#business` },
  areaServed: 'Greater Toronto Area',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Monthly Maintenance Plans',
    itemListElement: [
      {
        '@type': 'Offer',
        name: 'Basic Plan',
        price: '99',
        priceCurrency: 'CAD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '99',
          priceCurrency: 'CAD',
          unitText: 'month',
        },
      },
      {
        '@type': 'Offer',
        name: 'Complete Plan',
        price: '169',
        priceCurrency: 'CAD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '169',
          priceCurrency: 'CAD',
          unitText: 'month',
        },
      },
      {
        '@type': 'Offer',
        name: 'Premium Plan',
        price: '299',
        priceCurrency: 'CAD',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: '299',
          priceCurrency: 'CAD',
          unitText: 'month',
        },
      },
    ],
  },
};

export default function Home() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />
      <div className="min-h-screen bg-white">
        <Header />
        <Hero />
        <About />
        <Services />
        <WhyUs />
        <ServiceAreas />
        <Pricing />
        <Testimonials />
        <Contact />
        <Footer />
        <WhatsAppFloat />
      </div>
    </>
  );
}
